import styled from 'styled-components'

import { Button, CloseIcon } from '@blade/ui-kit'
import { ScreenGlowing } from '@root/features/premium/premium-information/ui/common/screen-glowing'
import { useTranslation } from '@root/shared/localization'
import { Dialog } from '@root/shared/ui-kit'

import { PremiumInfo } from '../common/premium-info'

import type { PremiumMeta } from '@root/entities/premium'
import type { ClassNameProps } from '@root/shared/react'
import type { DialogControlProps } from '@root/shared/ui-kit'

type PremiumInfoDialogProps = {
  product: PremiumMeta.PremiumProduct
} & ClassNameProps &
  DialogControlProps

export const PremiumInfoDialog: React.FC<PremiumInfoDialogProps> = ({
  className,
  onOpenChange,
  open,
  product,
}) => {
  const { t } = useTranslation()
  return (
    <Root
      describedBy={t('premium.info.button.check-token')}
      open={open}
      onOpenChange={onOpenChange}
      className={className}
      $product={product}
    >
      <Body>
        <ScreenGlowing product={product} />
        <Close onClick={() => onOpenChange?.(false)} size={20} />
        <Info product={product} />
        <ButtonClose
          onClick={() => onOpenChange?.(false)}
          color="primary"
          size="s"
          radius="rounded"
        >
          {t('premium.info.button.check-token')}
        </ButtonClose>
      </Body>
    </Root>
  )
}

const Root = styled(Dialog)<{
  $product: PremiumMeta.PremiumProduct
}>`
  --dialog-border-radius: 20px;

  background-color: var(--background-2);
  background-image: radial-gradient(
    50% 40% at 50% -15%,
    rgba(
        from
          ${({ $product }) =>
            $product === 'subscription'
              ? 'var(--subscriptionGlow-9)'
              : 'var(--boostGlow-9)'}
          r g b / 0.4
      )
      0%,
    transparent 100%
  );
  border: none;

  padding-block: 0 32px;

  display: grid;

  backdrop-filter: blur(50px);
  padding: 4px;
  overflow: hidden;
  border-radius: 17px;
`

const Body = styled.div`
  border: 1px solid var(--backgroundA-4);
  background-color: var(--appearanceA-3);

  display: grid;
  grid-template-rows: auto 1fr;

  border-radius: 13px;

  backdrop-filter: blur(50px);
  box-shadow: 0px 0px 0px 15px var(--appearance-1);
  overflow: hidden;
  padding-block: 20px;
  width: fit-content;
`

const Info = styled(PremiumInfo)`
  width: 600px;
  padding-inline: 20px;
`

const ButtonClose = styled(Button)`
  position: relative;
  z-index: 1;

  margin-top: 4px;
  justify-self: center;
  min-width: 240px;
`

const Close = styled(CloseIcon)`
  color: var(--textA-8);
  right: 10px;
  top: 10px;

  position: absolute;
  cursor: pointer;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--text-11);
  }
`
